'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

export type BannerGridProps = {
  className?: { main?: string; bannerLink?: string; bannerImage?: string };
};

export function BannerGrid(props: BannerGridProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();

  const { data: banners } = useSuspenseQuery({
    queryKey: [CacheKey.BANNERS, locale, { type: 'banner' }],
    queryFn: () =>
      fetchBanners({ locale, query: { where: { type: { equals: 'banner' } } } }),
  });

  const desktopBanners = banners.data?.filter(i => i.types?.includes('desktop') && i.image);
  const mobileBanners = banners.data?.filter(i => i.types?.includes('mobile') && i.image);

  if (!desktopBanners.length && !mobileBanners.length) return null;

  return (
    <>
      <div
        className={cn(
          'hidden grid-cols-2 gap-[16px] md:grid lg:grid-cols-4',
          'ea-banner-grid ea-banner-grid__desktop',
          props.className?.main,
        )}
      >
        {desktopBanners.map((banner, index) => {
          if (!banner.image) return null;
          return (
            <Link
              key={banner.name}
              href={banner.url ?? '#'}
              aria-label={banner.name || banner.image.name || t('text3524')}
              className={cn(
                'ea-banner-grid__link ea-banner-grid__link__desktop col-span-1 w-full',
                props.className?.bannerLink,
              )}
              prefetch={false}
            >
              <Image
                alt={resolveImageAlt(banner?.image, banner?.name, banner?.name || 'banner')}
                src={banner.image?.url ?? '/images/placeholder.png'}
                width={0}
                height={0}
                sizes="(min-width: 1280px) 300px, (min-width: 1024px) calc(25vw - 20px), (min-width: 768px) calc(50vw - 24px), calc(100vw - 32px)"
                quality={90}
                loading={index === 0 ? 'eager' : undefined}
                className={cn(
                  'h-auto w-full object-contain',
                  'ea-homepage-banner-grid__link-image ea-homepage-banner-grid__link-image__desktop',
                  props.className?.bannerImage,
                )}
              />
            </Link>
          );
        })}
      </div>
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-[16px] sm:grid-cols-2 md:hidden',
          'ea-banner-grid ea-banner-grid__mobile',
          props.className?.main,
        )}
      >
        {mobileBanners.map((banner, index) => {
          if (!banner.image) return null;
          return (
            <Link
              key={index}
              href={banner.url ?? '#'}
              aria-label={banner.name || banner.image.name || t('text3524')}
              className={cn(
                'ea-banner-grid__link ea-banner-grid__link__mobile col-span-1 w-full',
                props.className?.bannerLink,
              )}
              prefetch={false}
            >
              <Image
                alt={resolveImageAlt(banner?.image, banner?.name, banner?.name || 'banner')}
                src={banner.image?.url ?? '/images/placeholder.png'}
                height={0}
                width={0}
                sizes="(min-width: 640px) calc(50vw - 24px), calc(100vw - 32px)"
                quality={90}
                loading={index === 0 ? 'eager' : undefined}
                className={cn(
                  'h-auto w-full object-contain',
                  'ea-homepage-banner-grid__link-image ea-homepage-banner-grid__link-image__mobile',
                  props.className?.bannerImage,
                )}
              />
            </Link>
          );
        })}
      </div>
    </>
  );
}
